import { cn } from "@/lib/utils";

interface HighlightedTextProps {
  content: string;
  highlights: {
    text: string;
    className: string;
    hoverContent?: React.ReactNode;
  }[];
}

export const HighlightedText = ({ content, highlights }: HighlightedTextProps) => {
  const renderHighlightedContent = () => {
    let currentPosition = 0;
    const result: React.ReactNode[] = [];

    while (currentPosition < content.length) {
      // Find the next highlight that matches at the current position
      const matchingHighlight = highlights.find(h =>
        content.substring(currentPosition).startsWith(h.text)
      );

      if (matchingHighlight) {
        // Add the highlighted segment
        result.push(
          <span
            key={`highlight-${currentPosition}`}
            className={cn(
              "relative group cursor-pointer transition-colors",
              matchingHighlight.className
            )}
          >
            {matchingHighlight.text}
            {matchingHighlight.hoverContent && (
              <div className="absolute z-50 bottom-full left-0 mb-2 p-3 bg-white dark:bg-stone-800 rounded-lg shadow-lg border border-stone-200 dark:border-stone-700 text-sm w-80 opacity-0 group-hover:opacity-100 transition-opacity">
                {matchingHighlight.hoverContent}
              </div>
            )}
          </span>
        );
        currentPosition += matchingHighlight.text.length;
      } else {
        // Find the next highlight position
        const nextHighlightPosition = highlights.reduce((closest, h) => {
          const position = content.indexOf(h.text, currentPosition);
          if (position === -1) return closest;
          return closest === -1 ? position : Math.min(closest, position);
        }, -1);

        // Add the non-highlighted text up to the next highlight or the end
        const endPosition = nextHighlightPosition === -1 ? content.length : nextHighlightPosition;
        result.push(
          <span key={`text-${currentPosition}`}>
            {content.substring(currentPosition, endPosition)}
          </span>
        );
        currentPosition = endPosition;
      }
    }

    return result;
  };

  return <div className="relative whitespace-pre-wrap text-base/7">{renderHighlightedContent()}</div>;
};